/**
 * The composer: a bottom BAR, flush to the chat column's edges — one
 * full-width strip holding the multiline field with the toolbar row beneath
 * it (dictate + attach and, on a narrow column, the header's relocated
 * controls on the left, clear + send on the right). No floating card, no
 * inset gutters, no rounding: a top border separates it from the transcript
 * and it sits pinned against the column's bottom edge. The field itself
 * stays capped at the transcript's 760px so the bar reads as one column.
 *
 * CLONED from the Console (tc-console src/shell/Composer.tsx), with MUI icons
 * in place of FontAwesome (the LV shell doesn't carry the FontAwesome packs)
 * and the LV column's `compact` density. That file's warning applies here too:
 * this is the one piece of the assistant nobody should ever see a different
 * version of. Clear-the-chat wears the console's Refresh glyph (2026-10-28
 * parity) — clearing reads as "start the chat over", not "delete".
 *
 * The 3D cap no longer sits beside the input: while a reply is generating the
 * animated cap appears at the in-progress message's avatar slot instead
 * (chat/AssistantAvatar.tsx). The bar's top border/glow still tracks the
 * "thinking" state so the composer keeps reacting with the turn.
 */

import { useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { Box, IconButton, Paper, Stack, TextField, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import RefreshIcon from '@mui/icons-material/Refresh'

/** Every toolbar control keeps at least a 40×40 hit target (34×34 compact). */
const TARGET = { width: 40, height: 40 }
const TARGET_COMPACT = { width: 34, height: 34 }

export default function Composer({
  value,
  onChange,
  onSend,
  onStop,
  sending,
  placeholder = 'Ask anything…',
  autoFocus,
  /** Narrow-column mode: smaller buttons, tighter gutters. */
  compact = false,
  /** Voice input, attach, relocated header controls — the message-level
   *  actions at the toolbar's left. */
  toolbarStart,
  /** Empties the transcript. Rendered next to Send, beside the thing it clears. */
  onClear,
  /** A staged attachment IS the message — Send stays live with an empty field. */
  allowEmptySend,
}: {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  /** Aborts the in-flight reply. While a turn is streaming, Stop takes the
   *  send button's seat — sending is impossible mid-turn anyway. */
  onStop?: () => void
  sending: boolean
  placeholder?: string
  autoFocus?: boolean
  compact?: boolean
  toolbarStart?: ReactNode
  onClear?: () => void
  allowEmptySend?: boolean
}): ReactElement {
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  function handleSend() {
    onSend()
    // The caret stays in the field after sending — the next thought starts
    // typing immediately. rAF lets the cleared value render first.
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const sendDisabled = (!value.trim() && !allowEmptySend) || sending
  const target = compact ? TARGET_COMPACT : TARGET

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Paper
        elevation={0}
        square
        sx={(theme) => ({
          display: 'flex', flexDirection: 'column',
          borderTop: '1.5px solid',
          borderColor: sending ? 'primary.main' : 'divider',
          transition: 'border-color 0.3s ease',
          '@keyframes capGlow': {
            '0%,100%': { boxShadow: 'none' },
            '50%': {
              boxShadow: `0 -4px 18px ${alpha(theme.palette.primary.main, 0.22)}`,
            },
          },
          '@media (prefers-reduced-motion: no-preference)': {
            animation: sending ? 'capGlow 1.4s ease-in-out infinite' : 'none',
          },
        })}
      >
        <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
        <TextField
          fullWidth placeholder={placeholder} value={value}
          variant="standard"
          autoFocus={autoFocus}
          inputRef={inputRef}
          InputProps={{
            disableUnderline: true,
            sx: { minHeight: 44, alignItems: 'flex-start', px: 2, pt: 1, pb: 0.5, fontSize: compact ? '0.95rem' : '1.05rem' },
          }}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter and Ctrl/Cmd+Enter insert a line break
            // (the textarea's own default once we don't preventDefault —
            // Alina 2026-10-28, console parity: Ctrl+Enter sent the message,
            // it must newline); an in-progress IME composition must never
            // fire a send.
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              handleSend()
            }
          }}
          multiline maxRows={5}
        />
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ px: 1, pb: 0.5 }}>
          {toolbarStart}
          <Box sx={{ flex: 1 }} />
          {onClear && (
            <Tooltip title="Clear the conversation">
              <IconButton sx={target} onClick={onClear} aria-label="clear the conversation">
                {/* The refresh glyph, not a trash can (Alina 2026-10-28,
                    console parity): clearing reads as "start over". */}
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {sending && onStop ? (
            <Tooltip title="Stop the reply">
              <IconButton sx={target} onClick={onStop} aria-label="stop the reply">
                <StopCircleIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Send">
              {/* The span keeps the tooltip alive while the button is disabled. */}
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={sendDisabled}
                  aria-label="send message"
                  sx={{
                    ...target,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '10px',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        </Box>
      </Paper>
    </Box>
  )
}
